import { Dialog } from "@mui/material";
import { SearchEventAndOrganisationResult, SearchEventAndOrganisationResults } from "components/layout/home/SearchEventAndOrganisationResults";
import { makeStyles } from "components/providers/makeStyles";
import { Location } from "graphql/generated/types";
import { useCancellableLazyQuery } from "hooks/useCancellableLazyQuery";
import { CloseDialog, useDialogState } from "hooks/useDialogState";
import { useVisualViewportHeight } from "hooks/useVisualViewportHeight";
import { useRef, useState } from "react";
import { SearchTakeoverDialog, SearchTakeoverDialogContent, } from "../forms/SearchTakeoverDialog";
import { GET_EVENTS_BY_NAME, GET_ORGANISATIONS_BY_NAME } from "./SearchEventAndOrganisation.gql";
import { GetEventsByNameQuery, GetEventsByNameQueryVariables, GetOrganisationsByNameQuery, GetOrganisationsByNameQueryVariables } from "./SearchEventAndOrganisation.gql.types";
import { SearchEventAndOrganisationContainer } from "./SearchEventAndOrganisationContainer";

const useButtonStyles = makeStyles({
    button: {
        cursor: "pointer",
    }
});

export const SearchEventAndOrganisationButton: React.FCWithChildren = ({ children }) => {
    const classes = useButtonStyles();
    const [open, openDialog, closeDialog] = useDialogState();

    return (
        <>
            <div onClick={openDialog} role="button" className={classes.button}>{children}</div>
            <Dialog fullScreen open={open} onClose={closeDialog} aria-labelledby="event-organisation-search-dialog">
                <SearchEventAndOrganisationDialog closeDialog={closeDialog}/>
            </Dialog>
        </>
    );
};

const useInputStyles = makeStyles(theme => ({
    input: {
        "&&": {
            marginBottom: 0,
            border: 0,
            padding: theme.spacing(1, 1.5),
            opacity: 0.7
        }
    }
}));

export const SearchEventAndOrganisationInput = () => {
    const classes = useInputStyles();

    return (
        <SearchTakeoverDialog
            DialogComponent={SearchEventAndOrganisationDialog}
            inputPlaceholder="Search events and organisations"
            dialogTitleId="event-organisation-search-dialog"
            className={classes.input}
            showSearchIcon={false}
        />
    );
};

export const SearchEventAndOrganisationDialog: React.FC<{ closeDialog: CloseDialog }> = ({ closeDialog }) => {
    const searchRef = useRef<HTMLInputElement>(null);
    const [searchByName, setSearchByName] = useState("");
    const [searchByLocation, setSearchByLocation] = useState<Location | null>(null);
    const radius = 100; // Default radius in km

    const [eventLiveLazyQuery, { data: eventLiveData, loading: eventLiveLoading, variables: eventLiveVariables }] = useCancellableLazyQuery<GetEventsByNameQuery, GetEventsByNameQueryVariables>(GET_EVENTS_BY_NAME, { variables: { search: "", radius, limit: 100, eventLive: true }, fetchPolicy: "cache-and-network" }, true);
    const eventLiveSearch = eventLiveVariables?.search;
    const eventsLive = eventLiveData?.eventsByName || [];

    const [organisationLazyQuery, { data: orgData, loading: orgLoading, variables: organisationVariables }] = useCancellableLazyQuery<GetOrganisationsByNameQuery, GetOrganisationsByNameQueryVariables>(GET_ORGANISATIONS_BY_NAME, { variables: { search: "", limit: 100 }, fetchPolicy: "cache-and-network" }, true);
    const orgSearch = organisationVariables?.search;
    const organisations = orgData?.organisationsByName || [];

    const [eventLazyQuery, { data, loading: eventLoading, variables: eventVariables }] = useCancellableLazyQuery<GetEventsByNameQuery, GetEventsByNameQueryVariables>(GET_EVENTS_BY_NAME, { variables: { search: "", radius, limit: 100, eventLive: false }, fetchPolicy: "cache-and-network" }, true);
    const eventSearch = eventVariables?.search;
    const events = data?.eventsByName || [];

    const loading = eventLoading || orgLoading || eventLiveLoading;

    const containerHeight = useVisualViewportHeight();

    const refetchQueries = (search: string, location: Location | null) => {
        const locationInput = getLocationInput(location);
        eventLiveLazyQuery({ variables: { search, location: locationInput } });
        eventLazyQuery({ variables: { search, location: locationInput } });
        organisationLazyQuery({ variables: { search, location: locationInput } });
    };

    const onSearchByName = (search: string) => {
        setSearchByName(search);
        refetchQueries(search, searchByLocation);
    };

    const onSearchByLocation = (location: Location | null) => {
        setSearchByLocation(location);
        refetchQueries(searchByName, location);
    };

    const eventsLimit = Math.max(100 - eventsLive.length, 0);
    const limitedEvents = events.slice(0, eventsLimit);

    const searchResults: SearchEventAndOrganisationResult[] = [
        ...eventsLive.map(e => ({ type: "event", data: e } as SearchEventAndOrganisationResult)),
        ...organisations.map(o => ({ type: "organisation", data: o } as SearchEventAndOrganisationResult)),
        ...limitedEvents.map(e => ({ type: "event", data: e } as SearchEventAndOrganisationResult))
    ];

    return (
        <div style={{ maxHeight: containerHeight }}>
            <SearchTakeoverDialogContent
                dialogTitleId="event-organisation-search-dialog"
                searchRef={searchRef}
                placeholder="Search events and organisations"
                onSearch={onSearchByName}
                onLocationSearch={onSearchByLocation}
                loading={loading}
                closeDialog={closeDialog}
                SearchFormWrapper={SearchEventAndOrganisationContainer}
            >
                <SearchEventAndOrganisationResults
                    eventSearch={eventSearch}
                    orgSearch={orgSearch}
                    eventLiveSearch={eventLiveSearch}
                    locationSearch={searchByLocation}
                    loading={loading}
                    searchResults={searchResults}
                />
            </SearchTakeoverDialogContent>
        </div>
    );
};

function getLocationInput(location?: Location | null) {
    if (!location) return undefined;
    if (location.addressType === "country") return {
        country: location.country
    };
    if (location.location.lat !== undefined && location.location.lng !== undefined) return {
        coordinates: { lat: location.location.lat, lng: location.location.lng }
    };
    return undefined;
}
